import { useState, useEffect, useCallback } from 'react';
import {
  getExportFields,
  exportCsv,
  exportImages,
  ExportFieldOption,
} from '../utils/exportApi';
import { logger } from '../utils/logger';

interface ExportState {
  fields: ExportFieldOption[];
  selectedFields: Set<string>;
  loading: boolean;
  exporting: boolean;
  error: string | null;
  includePrivateFields: boolean;
  includeOriginals: boolean;
  includeWeb: boolean;
}

const initialState: ExportState = {
  fields: [],
  selectedFields: new Set(),
  loading: false,
  exporting: false,
  error: null,
  includePrivateFields: false,
  includeOriginals: true,
  includeWeb: true,
};

export const useExport = (ownerId?: string) => {
  const [state, setState] = useState<ExportState>(initialState);

  useEffect(() => {
    let cancelled = false;
    const loadFields = async () => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const fields = await getExportFields();
        if (cancelled) return;
        const allFieldNames = new Set(fields.map((f) => f.name));
        setState((prev) => ({
          ...prev,
          fields,
          selectedFields: allFieldNames,
          loading: false,
        }));
      } catch (error) {
        if (cancelled) return;
        logger.warn('Failed to load export fields', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: 'Failed to load export fields',
        }));
      }
    };
    loadFields();
    return () => { cancelled = true; };
  }, []);

  const toggleField = useCallback((fieldName: string) => {
    setState((prev) => {
      const next = new Set(prev.selectedFields);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else {
        next.add(fieldName);
      }
      return { ...prev, selectedFields: next };
    });
  }, []);

  const selectAllInCategory = useCallback((category: string) => {
    setState((prev) => {
      const categoryFields = prev.fields
        .filter((f) => f.category === category)
        .map((f) => f.name);
      const next = new Set(prev.selectedFields);
      categoryFields.forEach((f) => next.add(f));
      return { ...prev, selectedFields: next };
    });
  }, []);

  const deselectAllInCategory = useCallback((category: string) => {
    setState((prev) => {
      const categoryFields = new Set(
        prev.fields.filter((f) => f.category === category).map((f) => f.name)
      );
      const next = new Set(
        [...prev.selectedFields].filter((f) => !categoryFields.has(f))
      );
      return { ...prev, selectedFields: next };
    });
  }, []);

  const setIncludePrivateFields = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, includePrivateFields: value }));
  }, []);

  const setIncludeOriginals = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, includeOriginals: value }));
  }, []);

  const setIncludeWeb = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, includeWeb: value }));
  }, []);

  const handleExportCsv = useCallback(
    async (collectionId?: string, selectedIds?: string[]) => {
      const fields = [...state.selectedFields];
      if (fields.length === 0) {
        const msg = 'Select at least one field to export';
        setState((prev) => ({ ...prev, error: msg }));
        throw new Error(msg);
      }

      setState((prev) => ({ ...prev, exporting: true, error: null }));
      try {
        await exportCsv(fields, collectionId, ownerId, selectedIds);
        setState((prev) => ({ ...prev, exporting: false }));
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Export failed. Please try again.';
        setState((prev) => ({ ...prev, exporting: false, error: message }));
        throw err;
      }
    },
    [state.selectedFields, ownerId]
  );

  const handleExportImages = useCallback(
    async (collectionId?: string, selectedIds?: string[]) => {
      setState((prev) => ({ ...prev, exporting: true, error: null }));
      try {
        await exportImages(
          collectionId,
          state.includeOriginals,
          state.includeWeb,
          ownerId,
          selectedIds
        );
        setState((prev) => ({ ...prev, exporting: false }));
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Image export failed. Please try again.';
        setState((prev) => ({ ...prev, exporting: false, error: message }));
        throw err;
      }
    },
    [state.includeOriginals, state.includeWeb, ownerId]
  );

  return {
    ...state,
    toggleField,
    selectAllInCategory,
    deselectAllInCategory,
    setIncludePrivateFields,
    setIncludeOriginals,
    setIncludeWeb,
    exportCsv: handleExportCsv,
    exportImages: handleExportImages,
  };
};
